import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { Button } from './ui/Button';
import { EyeOff, Brain } from 'lucide-react';
interface SlideEngagementProps {
  onContinue: () => void;
}
// Slide 5 — Engagement Yield Calculator
export function SlideCalculator({
  onContinue
}: SlideEngagementProps) {
  const [reach, setReach] = useState(5000);
  const [engagementRate, setEngagementRate] = useState(20);
  const [campaignsPerYear, setCampaignsPerYear] = useState(16); // 4 brands × 4 campaigns
  const engaged = Math.round(reach * (engagementRate / 100));
  const annualInsights = engaged * campaignsPerYear;
  const costPerInsight = Math.round(28000 / annualInsights * 100) / 100; // $28K partnership / insights
  const engagedSpring = useSpring(0, {
    bounce: 0,
    duration: 600
  });
  const annualSpring = useSpring(0, {
    bounce: 0,
    duration: 800
  });
  const displayEngaged = useTransform(engagedSpring, latest => Math.round(latest).toLocaleString());
  const displayAnnual = useTransform(annualSpring, latest => Math.round(latest).toLocaleString());
  useEffect(() => {
    engagedSpring.set(engaged);
    annualSpring.set(annualInsights);
  }, [engaged, annualInsights]);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-38">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-39">
        <span className="text-teal-400 text-xs uppercase tracking-widest font-medium" data-id="element-40">
          Engagement Yield
        </span>
        <h2 className="text-3xl md:text-5xl font-serif text-white mt-3 mb-3" data-id="element-41">
          How much of the budget becomes{' '}
          <span className="text-teal-400" data-id="element-42">measurable HCP behaviour?</span>
        </h2>
        <p className="text-base text-white/60 max-w-3xl" data-id="element-43">
          Static content is seen and forgotten. Interactive content turns every
          view into a data point you can act on.
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8" data-id="element-44">
        {/* LEFT — Inputs */}
        <div className="lg:col-span-5 space-y-4" data-id="element-45">
          <div className="bg-navy-900/70 p-5 rounded-2xl border border-navy-600 space-y-4" data-id="element-46">
            <p className="text-white/40 text-[10px] uppercase tracking-widest font-medium" data-id="element-47">
              Your assumptions per campaign
            </p>

            <div className="space-y-1.5" data-id="element-48">
              <div className="flex justify-between text-sm" data-id="element-49">
                <label className="text-white/70" data-id="element-50">HCP reach</label>
                <span className="text-white font-medium" data-id="element-51">
                  {reach.toLocaleString()}
                </span>
              </div>
              <input type="range" min="1000" max="15000" step="500" value={reach} onChange={e => setReach(Number(e.target.value))} className="w-full" data-id="element-52" />
            </div>

            <div className="space-y-1.5" data-id="element-53">
              <div className="flex justify-between text-sm" data-id="element-54">
                <label className="text-white/70" data-id="element-55">
                  Engagement rate (interactive)
                </label>
                <span className="text-white font-medium" data-id="element-56">
                  {engagementRate}%
                </span>
              </div>
              <input type="range" min="5" max="50" step="1" value={engagementRate} onChange={e => setEngagementRate(Number(e.target.value))} className="w-full" data-id="element-57" />
            </div>

            <div className="space-y-1.5" data-id="element-58">
              <div className="flex justify-between text-sm" data-id="element-59">
                <label className="text-white/70" data-id="element-60">
                  Campaigns / year (4 brands)
                </label>
                <span className="text-white font-medium" data-id="element-61">
                  {campaignsPerYear}
                </span>
              </div>
              <input type="range" min="4" max="40" step="1" value={campaignsPerYear} onChange={e => setCampaignsPerYear(Number(e.target.value))} className="w-full" data-id="element-62" />
            </div>
          </div>

          {/* The math, made explicit */}
          <div className="bg-navy-800/40 border border-navy-700 rounded-xl p-4 text-sm" data-id="element-63">
            <p className="text-white/40 text-[10px] uppercase tracking-widest font-medium mb-2" data-id="element-64">
              The math
            </p>
            <div className="space-y-1 text-white/70 font-mono text-xs" data-id="element-65">
              <div data-id="element-66">
                {reach.toLocaleString()} reach × {engagementRate}% ={' '}
                <span className="text-teal-400 font-bold" data-id="element-67">
                  {engaged.toLocaleString()}
                </span>{' '}
                engaged / campaign
              </div>
              <div data-id="element-68">
                {engaged.toLocaleString()} × {campaignsPerYear} campaigns ={' '}
                <span className="text-teal-400 font-bold" data-id="element-69">
                  {annualInsights.toLocaleString()}
                </span>{' '}
                / year
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT — The comparison: what changes */}
        <div className="lg:col-span-7 space-y-4" data-id="element-70">
          <p className="text-white/40 text-xs uppercase tracking-widest font-medium" data-id="element-71">
            The upside is the gap
          </p>

          {/* Static today */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="bg-navy-800/60 border border-navy-600 rounded-2xl p-5" data-id="element-72">
            <div className="flex items-center gap-3 mb-3" data-id="element-73">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center justify-center" data-id="element-74">
                <EyeOff className="w-4 h-4 text-red-400/80" data-id="element-75" />
              </div>
              <div className="flex-1" data-id="element-76">
                <div className="text-white/40 uppercase text-[10px] tracking-widest font-medium" data-id="element-77">
                  Static content today
                </div>
                <div className="text-white font-semibold" data-id="element-78">
                  Reach without understanding
                </div>
              </div>
              <div className="text-right" data-id="element-79">
                <div className="text-3xl font-serif text-white/70" data-id="element-80">0</div>
                <div className="text-white/40 text-[10px]" data-id="element-81">measurable HCPs</div>
              </div>
            </div>
            <p className="text-white/50 text-sm" data-id="element-82">
              You reach {reach.toLocaleString()} HCPs but learn nothing about
              who engaged, what they understood, or what to do next.
            </p>
          </motion.div>

          {/* Interactive with Snakker */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.15
        }} className="bg-teal-400/5 border border-teal-400/30 rounded-2xl p-5" data-id="element-83">
            <div className="flex items-center gap-3 mb-3" data-id="element-84">
              <div className="w-10 h-10 rounded-lg bg-teal-400/15 border border-teal-400/30 flex items-center justify-center" data-id="element-85">
                <Brain className="w-4 h-4 text-teal-400" data-id="element-86" />
              </div>
              <div className="flex-1" data-id="element-87">
                <div className="text-teal-400 uppercase text-[10px] tracking-widest font-medium" data-id="element-88">
                  Interactive with Snakker
                </div>
                <div className="text-white font-semibold" data-id="element-89">
                  Every engagement = a data point
                </div>
              </div>
              <div className="text-right" data-id="element-90">
                <motion.div className="text-3xl font-serif text-teal-400" data-id="element-91">
                  {displayEngaged}
                </motion.div>
                <div className="text-teal-300/70 text-[10px]" data-id="element-92">
                  known HCPs / campaign
                </div>
              </div>
            </div>
            <p className="text-white/70 text-sm" data-id="element-93">
              For each engaged HCP you capture: knowledge gaps, answer choices,
              message preferences. Reusable for the next campaign, segmentable
              by country and brand.
            </p>
          </motion.div>

          {/* Annual rollup */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="bg-teal-400/10 border border-teal-400/30 rounded-2xl p-5 flex items-center gap-5" data-id="element-94">
            <div data-id="element-95">
              <div className="text-white/50 text-xs uppercase tracking-widest font-medium" data-id="element-96">
                Annual yield
              </div>
              <div className="flex items-baseline gap-2 mt-1" data-id="element-97">
                <motion.span className="text-5xl md:text-6xl font-serif text-teal-400 tracking-tight" data-id="element-98">
                  {displayAnnual}
                </motion.span>
                <span className="text-white/50 text-sm" data-id="element-99">
                  insight-generating interactions
                </span>
              </div>
            </div>
            <div className="border-l border-teal-400/30 pl-5" data-id="element-100">
              <div className="text-white/50 text-xs uppercase tracking-widest font-medium" data-id="element-101">
                Cost per insight
              </div>
              <div className="text-2xl font-serif text-white mt-1" data-id="element-102">
                ~${costPerInsight}
              </div>
              <div className="text-white/40 text-[10px]" data-id="element-103">
                on a $28K partnership
              </div>
            </div>
          </motion.div>

          <p className="text-white/60 leading-relaxed text-sm border-l-2 border-teal-400/40 pl-3 py-1" data-id="element-104">
            The goal isn't more content. It's{' '}
            <span className="text-white font-medium" data-id="element-105">
              turning the same budget into measurable, compounding HCP learning
            </span>{' '}
            — across brands, countries and campaigns.
          </p>
        </div>
      </div>

      <div className="flex justify-end mt-6" data-id="element-106">
        <Button onClick={onContinue} data-id="element-107">See the proof →</Button>
      </div>
    </div>;
}